// Stores the passphrase-wrapped private key only - never the raw key.
// Same Argon2id+SecretBox blob format the CLI writes to disk (cryptocore/keys.py).
// IndexedDB over localStorage/cookies: origin-scoped, not sent on every request,
// not trivially readable by third-party scripts.
const DB_NAME = "secure-crypt";
const DB_VERSION = 1;
const STORE_NAME = "wrapped_keys";

export interface StoredKeyRecord {
  id: string; // arbitrary local label, e.g. "default"
  publicKey: Uint8Array; // NOT secret
  wrapped: Uint8Array; // passphrase-wrapped private key blob (SCPK format)
  createdAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveWrappedKey(record: StoredKeyRecord): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadWrappedKey(id: string): Promise<StoredKeyRecord | undefined> {
  const db = await openDb();
  const result = await new Promise<StoredKeyRecord | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result as StoredKeyRecord | undefined);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

export async function listWrappedKeys(): Promise<StoredKeyRecord[]> {
  const db = await openDb();
  const result = await new Promise<StoredKeyRecord[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as StoredKeyRecord[]);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

export async function deleteWrappedKey(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
